import React from 'react';

import { Box } from '@mui/material';
import { Outlet } from 'react-router';

import { DropdownNavigationMenu, DropdownNavigationRoute, PageLayout } from 'src/components/common';
import { pageLayoutPaperSx } from 'src/constants/styles';
import { useScreenInfo } from 'src/hooks/common';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { SxProp } from 'src/types/mui';

import {
  REPORT_ROUTES_CLEARVERIFY_ADMIN,
  REPORT_ROUTES_CLEARVERIFY_MANAGER,
  REPORT_ROUTES_CONSUMER,
  REPORT_ROUTES_EMPLOYER_ADMIN,
  REPORT_ROUTES_EMPLOYER_MANAGER,
  REPORT_ROUTES_PEO_ADMIN,
  REPORT_ROUTES_PEO_MANAGER,
  REPORT_ROUTES_PEOPP,
  REPORT_ROUTES_VERIFIER,
} from './constants';

const renderReportsPageLayout = (routes: DropdownNavigationRoute[], isMobile?: boolean) => {
  if (isMobile) {
    return (
      <Box sx={mobileContentWrapperStyles}>
        <Outlet />
      </Box>
    );
  }

  const titleContent =
    routes.length === 1 ? routes[0]?.titleContent : <DropdownNavigationMenu routes={routes} />;

  return (
    <PageLayout titleContent={titleContent} paperSx={pageLayoutPaperSx}>
      <Outlet />
    </PageLayout>
  );
};

export const ReportsOutlet: React.FC = () => {
  const { isMobile } = useScreenInfo();

  const {
    isPlatformAdmin,
    isPlatformManager,
    isEmployee,
    hasVerifierRoles,
    isEmployerAdmin,
    isEmployerManager,
    isPeoSuperAdmin,
    isPeoAdmin,
    isPeoManager,
    isPeoPayrollProviderUser,
  } = useCurrentUserPermissions();

  if (isPlatformAdmin) {
    return renderReportsPageLayout(REPORT_ROUTES_CLEARVERIFY_ADMIN);
  }

  if (isPlatformManager) {
    return renderReportsPageLayout(REPORT_ROUTES_CLEARVERIFY_MANAGER);
  }

  if (hasVerifierRoles) {
    return renderReportsPageLayout(REPORT_ROUTES_VERIFIER, isMobile);
  }

  if (isPeoSuperAdmin || isPeoAdmin) {
    return renderReportsPageLayout(REPORT_ROUTES_PEO_ADMIN);
  }

  if (isEmployerAdmin) {
    return renderReportsPageLayout(REPORT_ROUTES_EMPLOYER_ADMIN);
  }

  if (isPeoManager) {
    return renderReportsPageLayout(REPORT_ROUTES_PEO_MANAGER);
  }

  if (isEmployerManager) {
    return renderReportsPageLayout(REPORT_ROUTES_EMPLOYER_MANAGER);
  }

  if (isPeoPayrollProviderUser) {
    return renderReportsPageLayout(REPORT_ROUTES_PEOPP);
  }

  if (isEmployee) {
    return renderReportsPageLayout(REPORT_ROUTES_CONSUMER, isMobile);
  }

  return <Outlet />;
};

const mobileContentWrapperStyles: SxProp = {
  zIndex: '1',
  position: 'relative',
  height: '100%',
  width: '100%',
  overflowY: 'scroll',
  p: 2,
  pt: 0,
};
